import { pick, set } from 'lodash';
import { TextAnimationFrame } from '../components/animation-frame/animation.types';
import { SoundAnimationFrameObject } from './sound-animation-frame';
import { AnimationEnvironment } from '../../../services/animation/animation.types';

export class TextAnimationFrameObject<
  T extends TextAnimationFrame = TextAnimationFrame,
> extends SoundAnimationFrameObject<T> {
  get sourceType() {
    return this.currentLanguage
      ? this.frame.langs[this.currentLanguage]?.sourceType
      : this.frame.sourceType;
  }

  set sourceType(val: TextAnimationFrame['sourceType']) {
    if (this.currentLanguage) {
      set(this.frame, ['langs', this.currentLanguage, 'sourceType'], val);
    } else {
      this.frame.sourceType = val;
    }
  }

  get audioFileUrl() {
    const descriptor = this.frame.langs?.[this.currentLanguage] || this.frame;
    switch (descriptor.sourceType) {
      case 'recorded':
        return descriptor.recordFileUrl;
      case 'uploaded':
        return descriptor.uploadFileUrl;
      case 'generated':
      default:
        return descriptor.soundFileUrl;
    }
  }

  set audioFileUrl(val: string) {
    if (this.currentLanguage) {
      this.frame.langs[this.currentLanguage] ||= {
        text: '',
      };
      switch (this.sourceType) {
        case 'recorded':
          this.frame.langs[this.currentLanguage].recordFileUrl = val;
          break;
        case 'uploaded':
          this.frame.langs[this.currentLanguage].uploadFileUrl = val;
          break;
        case 'generated':
        default:
          this.frame.langs[this.currentLanguage].soundFileUrl = val;
      }
    } else {
      switch (this.sourceType) {
        case 'recorded':
          this.frame.recordFileUrl = val;
          break;
        case 'uploaded':
          this.frame.uploadFileUrl = val;
          break;
        case 'generated':
        default:
          this.frame.soundFileUrl = val;
      }
    }
  }

  get _duration() {
    return this.frame.duration;
  }

  get audioDuration() {
    const descriptor = this.frame.langs?.[this.currentLanguage] || this.frame;
    switch (descriptor.sourceType) {
      case 'recorded':
        return (descriptor.recordFileDuration || 1) * this.frame.duration;
      case 'uploaded':
        return (descriptor.uploadFileDuration || 1) * this.frame.duration;
      case 'generated':
      default:
        return (descriptor.audioDuration || 1) * this.frame.duration;
    }
  }

  set audioDuration(val: number) {
    if (this.currentLanguage) {
      this.frame.langs[this.currentLanguage] ||= {
        text: '',
      };
      switch (this.sourceType) {
        case 'recorded':
          console.log(
            'set.audioDuration > currentLanguage',
            this.currentLanguage,
            'val',
            val,
          );
          this.frame.langs[this.currentLanguage].recordFileDuration = val;
          break;
        case 'uploaded':
          this.frame.langs[this.currentLanguage].uploadFileDuration = val;
          break;
        case 'generated':
        default:
          this.frame.langs[this.currentLanguage].audioDuration = val;
      }
    } else {
      switch (this.sourceType) {
        case 'recorded':
          this.frame.recordFileDuration = val;
          break;
        case 'uploaded':
          this.frame.uploadFileDuration = val;
          break;
        case 'generated':
        default:
          this.frame.audioDuration = val;
      }
    }
  }

  get text() {
    if (this.currentLanguage) {
      return this.frame.langs?.[this.currentLanguage]?.text;
    }
    return this.frame.text;
  }

  set text(val: string) {
    if (this.currentLanguage) {
      this.frame.langs[this.currentLanguage] ||= {
        text: val,
      };
      set(this.frame, ['langs', this.currentLanguage, 'text'], val);
    } else {
      this.frame.text = val;
    }
  }

  get type() {
    return 'text';
  }

  get minimalFrameObject() {
    return {
      ...super.minimalFrameObject,
      ...pick(this.frame, ['text', 'subtitle', 'langs']),
    };
  }

  envChanged(env: AnimationEnvironment) {
    this.audioService.initSoundBuffer(this.audioFileUrl);
  }

  async _animate(env: AnimationEnvironment) {
    this.rootMainFrame.addSubtitle(this.text);
    await super._animate(env);
    this.rootMainFrame.removeSubtitle();
  }

  _animtionId: string;

  async localAnimate(_inverse?: boolean, _timeScale?: number) {
    this.maxIncrement = Math.ceil(this.duration * 60);
    this.currentIncrement = 0;

    return new Promise<void>(resolve => {
      this._animtionId = Math.random().toString() + Math.random().toString();
      this.playSound();
      this.service.animationFrames[this._animtionId] = increment => {
        this.currentIncrement += increment;
        if (this.currentIncrement > this.maxIncrement) {
          resolve();
        }
      };
    });
  }

  setSourceType(source: TextAnimationFrame['sourceType']) {
    this.sourceType = source;
    this.save();
  }

  get generatedFileName() {
    return !this.currentLanguage
      ? `${this.id}.mp3`
      : `${this.id}.${this.currentLanguage}.mp3`;
  }

  get recordedFileName() {
    return !this.currentLanguage
      ? `${this.id}.recorded.mp3`
      : `${this.id}.${this.currentLanguage}.recorded.mp3`;
  }

  get uploadedFileNamePrefix() {
    return !this.currentLanguage
      ? `${this.id}.uploaded`
      : `${this.id}.${this.currentLanguage}.uploaded`;
  }

  saveAudioFile(fileName: string, duration: number) {
    this.audioFileUrl = fileName;
    this.audioDuration = duration;
    this.save();
  }
}
